/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {RouteComponentProps, useHistory, withRouter} from "react-router-dom";
import {ClockIcon, CloudArrowDownIcon, ExclamationTriangleIcon, UserIcon} from "@heroicons/react/24/outline";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import {currencyNumberFormat} from "utils/utility";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {Reservation} from "types/entities/Reservation";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PackageItemItemType} from "types/entities/PackageItem";
import SoldTimeslots from "pages/Front/Booking/Seller/SoldTimeslots";
import BookingAction from "store/booking/BookingAction";
import {toast} from "react-toastify";
import BookingSellerPackagesCartItemsTable
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerPackagesCartItemsTable";
import BookingSellerPackageCartItemVouchersTable
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerPackageCartItemVouchersTable";
import clsx from "clsx";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import BookingSellerReservationInfoCard
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerReservationInfoCard";
import BookingSellerReservationLeadOrigin
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerReservationLeadOrigin";
import BookingSellerReservationOfferStatusCard
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerReservationOfferStatusCard";
import BookingSellerNotes
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerNotes";
import BookingSellerJournal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerJournal";
import SuspenseLoader from "components/SuspenseLoader";
import BookingSellerRebookingModal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerRebookingModal";
import {closeModal, openModal} from "helpers/actions";
import BookingSellerAssignSellerModal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerAssignSellerModal";
import BookingSellerDuplicateOfferModal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerDuplicateOfferModal";
import {ChevronDoubleRightIcon, ListBulletIcon} from "@heroicons/react/24/solid";
import {ModalSize} from "pages/Front/components/Popup";
import BookingSellerPaymentPlanModal
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerPaymentPlanModal";
import UiButton, {UiButtonStyle} from "pages/Front/components/Ui/components/UiButton";
import AlertConfirmation from "pages/Front/Registration/components/AlertConfirmation";
import BookingSellerPaymentPlan
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerPaymentPlan/BookingSellerPaymentPlan";
import {downloadHandler} from "utils/sellerUtils";
import ReservationInvoiceNote from "pages/Front/Booking/Seller/components/ReservationInvoiceNote";
import ReservationOfferNote from "pages/Front/Booking/Seller/components/ReservationOfferNote";

interface Params {
    reservation_id: string;
}

interface BookingSellerReservationDetailProps extends RouteComponentProps<Params> {
}

const BookingSellerReservationDetail = (props: BookingSellerReservationDetailProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const reservation_id = +props.match.params.reservation_id;
    const reservations = useTypedSelector(state => state.seller.reservations);
    const reservation = reservations.find(reservation => reservation.id === reservation_id) as Reservation; // this is always a reservation
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [reason, setReason] = useState<string | null>(null);

    const fetchReservationHandler = () => {
        setIsLoading(true);
        dispatch(BookingLoginAction.fetchSellerReservationDetail(reservation_id))
            .then(r => {
                if (r?.reservation) {
                    setReason(null);
                } else if (r?.reason) {
                    setReason(r.reason);
                }
            })
            .catch(err => {
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchReservationHandler();
    }, []);

    if (!reservation || reason) {
        return (
            <div className={"w-full flex justify-center"}>
                <CardComponent>
                    <div className={"BookingSellerReservationDetail w-full p-4"}>
                        <div className={"w-full text-left space-y-2"}>
                            <h1 className={"text-gray-900 text-2xl font-bold mb-0.5"}>{reason ? t("Reservation error") : t("Reservation not found")}</h1>
                            <p className={"inline-flex items-center font-semibold text-gray-600 mb-4"}>
                                <ExclamationTriangleIcon className={"text-red-500 w-7 h-7 mr-4"} />
                                <span>{reason ? reason : t("Please go back to previous page")}</span>
                            </p>
                        </div>
                    </div>
                </CardComponent>
            </div>
        );
    }

    const packagesCartItemsOutstanding =
        reservation.packagesCart &&
        reservation.packagesCart.packagesCartItems.filter((packagesCartItem: PackagesCartItem) => {
            if (packagesCartItem.packageItem?.type === PackageItemItemType.BasePrice) {
                return false;
            }
            if (packagesCartItem.packageItem?.activity_id === null) {
                return true;
            }
        });

    const continueWithBookingConfirmation = () => {
        if (!reservation.company || !reservation.packagesCart) {
            toast.error("Cannot rebook this reservation directly, contact support");
            return;
        }

        if (reservation.company) {
            dispatch(BookingAction.setCustomer(reservation.company));
        }
        if (reservation.packagesCart) {
            dispatch(BookingAction.setPackageCart(reservation.packagesCart));
        }

        toast.success("Redirecting to reservation booking");
        return history.push("/booking/seller/new-reservation");
    };

    const openHandler = (url: string, filename: string) => {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Cleanly remove the link from the DOM
    };

    const openPDFHandler = (netto_pdf: boolean) => {
        dispatch(BookingAction.fetchReservationOffer(reservation.id, netto_pdf))
            .then(r => {
                openHandler(r.offer, r.download_as);
                toast.success(t("Download PDF"));
            }).catch(err => {
            if (err?.status && err?.status === 401) {
                history.replace("/booking/seller/login");
            }
        });
    };

    const downloadPDFHandler = (netto_pdf: boolean) => {
        dispatch(BookingAction.fetchReservationOffer(reservation.id, netto_pdf))
            .then(r => {
                downloadHandler(r.offer, r.download_as).then(() => {});
                toast.success(t("Download PDF"));
            }).catch(err => {
            if (err?.status && err?.status === 401) {
                history.replace("/booking/seller/login");
            }
        });
    };

    const downloadNettoPDFHandler = () => {
        downloadPDFHandler(true);
    };

    const downloadBruttoPDFHandler = () => {
        AlertConfirmation({
            message: t("Are you sure?"),
            onClick: () => {
                downloadPDFHandler(false);
            },
            title: t("Do you really want to piss off Jan?"),
        });
    };

    const openNettoPDFHandler = () => {
        openPDFHandler(true);
    };

    const openBruttoPDFHandler = () => {
        AlertConfirmation({
            message: t("Are you sure?"),
            onClick: () => {
                openPDFHandler(false);
            },
            title: t("Do you really want to piss off Jan?"),
        });
    };


    const onOpenRebookingModal = () => {
        dispatch(
            openModal(<BookingSellerRebookingModal reservation_id={reservation.id} />, {
                modalSize: ModalSize.Scrollable,
                closeModal: () => {
                    dispatch(closeModal());
                },
            }),
        );
    };

    const onOpenAssignSellerModal = () => {
        dispatch(
            openModal(<BookingSellerAssignSellerModal reservation_id={reservation.id} />, {
                modalSize: ModalSize.Scrollable,
                closeModal: () => {
                    dispatch(closeModal());
                },
            }),
        );
    };

    const onOpenDuplicateOfferModal = () => {
        dispatch(
            openModal(<BookingSellerDuplicateOfferModal reservation_id={reservation.id} />, {
                modalSize: ModalSize.Scrollable,
                closeModal: () => {
                    dispatch(closeModal());
                },
            }),
        );
    };

    const onOpenPaymentPlanModal = () => {
        dispatch(
            openModal(<BookingSellerPaymentPlanModal reservation_id={reservation.id} />, {
                modalSize: ModalSize.Scrollable,
                closeModal: () => {
                    dispatch(closeModal());
                },
            }),
        );
    };

    const packagesCartItems =
        reservation?.packagesCart && "packagesCartItems" in reservation.packagesCart
            ? reservation.packagesCart.packagesCartItems.sort((a, b) => (a.packageItem!.article_id > b.packageItem!.article_id ? 1 : -1))
            : [];

    const packagesCartItemVouchers =
        reservation?.packagesCart && "packagesCartItemVouchers" in reservation.packagesCart ? reservation.packagesCart?.packagesCartItemVouchers : [];

    return (
        <div className={"BookingSellerReservationDetail w-full gap-y-4 relative flex flex-col"}>

            {isLoading && <div
                className={"absolute inset-0 flex justify-center items-center h-full w-full bg-white/50 backdrop-blur-sm z-10"}>
                <SuspenseLoader /></div>}

            <div className={"w-full text-left mb-4"}>
                <h1 className={"text-gray-900 text-2xl font-bold mb-0.5"}>{t("Reservation detail")}</h1>
                <div className={"flex flex-col gap-2"}>
                    <p className={"inline-flex items-center font-semibold text-gray-600"}>
                        <span>{t("In the details of the reservation, you can select, add or edit the persons with whom you wish to visit ArboPark.")}</span>
                    </p>
                    <div className={"flex flex-row flex-wrap gap-2"}>


                        <div className={"flex flex-row"}>
                            {/*<UiButton*/}
                            {/*    onClick={downloadNettoPDFHandler}*/}
                            {/*    title={"Download Netto PDF"}*/}
                            {/*    icon={*/}
                            {/*        <CloudArrowDownIcon className={"w-4 h-4 mr-1.5"} />*/}
                            {/*    }*/}
                            {/*/>*/}
                            <UiButton
                                style={UiButtonStyle.Gray}
                                title={"Open Netto PDF"}
                                onClick={openNettoPDFHandler}
                            />
                        </div>

                        <div className={"flex flex-row"}>
                            {/*<UiButton*/}
                            {/*    onClick={downloadBruttoPDFHandler}*/}
                            {/*    title={"Download PDF"}*/}
                            {/*    icon={*/}
                            {/*        <CloudArrowDownIcon className={"w-4 h-4 mr-1.5"} />*/}
                            {/*    }*/}
                            {/*/>*/}
                            <UiButton
                                style={UiButtonStyle.Gray}
                                title={"Open Brutto PDF"}
                                onClick={openBruttoPDFHandler}
                            />
                        </div>
                        <div>
                            <UiButton
                                icon={<ClockIcon className={"w-4 h-4 mr-1.5"} />
                                }
                                style={UiButtonStyle.Red}
                                title={"Rebooking"}
                                onClick={onOpenRebookingModal}
                            />
                        </div>
                        <div>
                            <UiButton
                                icon={<UserIcon className={"w-4 h-4 mr-1.5"} />}
                                title={"Assign seller"}
                                onClick={onOpenAssignSellerModal}
                            />
                        </div>
                        <div>
                            <UiButton
                                icon={
                                    <ChevronDoubleRightIcon className={"w-4 h-4 mr-1.5"} />
                                }
                                title={"Duplicate offer"}
                                onClick={onOpenDuplicateOfferModal}
                            />
                        </div>
                        <div>
                            <UiButton
                                icon={
                                    <ListBulletIcon className={"w-4 h-4 mr-1.5"} />

                                }
                                title={"Payment plan"}
                                onClick={onOpenPaymentPlanModal}
                            />
                        </div>
                    </div>

                </div>
            </div>

            <div className={"flex-grow flex flex-col w-full gap-6"}>
                <BookingSellerReservationInfoCard className={"!mb-0"} reservation={reservation} />
                <BookingSellerReservationOfferStatusCard className={"!mb-0"} reservation={reservation} />

                <BookingSellerPackagesCartItemsTable
                    isAccessTicket={true}
                    packagesCartItems={packagesCartItems.filter(packagesCartItem => packagesCartItem.packageItem?.type != PackageItemItemType.Upsell)}
                    reservation={reservation}
                    title={t("Access tickets")}
                />

                <BookingSellerPackagesCartItemsTable
                    packagesCartItems={packagesCartItems.filter(packagesCartItem => packagesCartItem.packageItem?.type == PackageItemItemType.Upsell)}
                    reservation={reservation}
                    title={t("Upsells")}
                />

                <BookingSellerPackageCartItemVouchersTable
                    packagesCartItemVouchers={packagesCartItemVouchers ? packagesCartItemVouchers : []}
                    reservation={reservation}
                />

                <BookingCard className={clsx("ReservationsForDate !px-0 !py-0")}>
                    <BookingCardTitle className={"!font-bold text-xl !text-black px-3 py-2.5 xl:px-3 xl:py-3 border-b"}
                                      title={t("Summary")} />
                    <div className={"flex flex-row justify-between py-4 px-3 text-lg"}>
                        <div className={"font-medium"}>{t("Total price")}</div>
                        <b>{reservation?.packagesCart?.price ? currencyNumberFormat(reservation.packagesCart.price) : "---"}</b>
                    </div>
                </BookingCard>
            </div>

            <div className={"grid grid-cols-1 md:grid-cols-4 gap-6"}>
                <BookingSellerReservationLeadOrigin
                    className={"col-span-1 md:col-span-4 lg:col-span-2 !mb-0 self-baseline"}
                    reservation={reservation} />
                {reservation.packagesCart && (
                    <SoldTimeslots
                        centered={true}
                        className={"col-span-1 md:col-span-4 lg:col-span-2 !mb-0 self-baseline"}
                        packagesCart={reservation.packagesCart}
                        packagesCartItemsOutstanding={packagesCartItemsOutstanding}
                        packagesCategory={PackageCategory.Event}
                        onClick={onOpenRebookingModal}
                    />
                )}
                <BookingSellerNotes className={"col-span-1 md:col-span-2"} reservation={reservation} />
                <BookingSellerJournal className={"col-span-1 md:col-span-2"} reservation={reservation} />
            </div>

            <div className={"grid grid-cols-1 md:grid-cols-4 gap-6"}>
                <BookingSellerPaymentPlan className={"col-span-1 md:col-span-2"} reservation={reservation} />
                <ReservationInvoiceNote className={"col-span-1 md:col-span-2"} reservation={reservation} />
                <ReservationOfferNote className={"col-span-1 md:col-span-2"} reservation={reservation} />
            </div>
        </div>
    );
};

export default withRouter(BookingSellerReservationDetail);
